import React from "react"
import "ag-grid-enterprise"

// components
import { AgGridReact } from "ag-grid-react"
import PropTypes from "prop-types"
import Presets from "components/Grid/Presets/Presets"
import Loading from "components/Loading/Loading"
import ComplianceThreshold from "components/Grid/ComplianceThreshold/ComplianceThreshold"

// column types
import dateColumn from "./columnTypes/dateColumn"

// cell renderers
import Date from "components/ag-grid-cell-renderers/Date/Date"
import DateSecond from "components/ag-grid-cell-renderers/DateSecond/DateSecond"
import { Badge, RecordStatusBadge } from "components-design-system"
import WorkflowStatusBadgeCell from "components-design-system/badges/Badge/WorkflowStatusBadge/WorkflowStatusBadgeCell/WorkflowStatusBadgeCell"
import ActionItemStatus from "components/ag-grid-cell-renderers/ActionItemStatus/ActionItemStatus"
import StudiesCellRenderer from "components/ag-grid-cell-renderers/StudiesCellRenderer/StudiesCellRenderer"
import RequestsCellRenderer from "containers/requests/components/RequestsCellRenderer"
import FavoriteStudyCellRenderer from "components/ag-grid-cell-renderers/FavoriteCellRenderer/FavoriteStudyCellRenderer/FavoriteStudyCellRenderer"
import FavoriteRequestCellRenderer from "components/ag-grid-cell-renderers/FavoriteCellRenderer/FavoriteRequestCellRenderer/FavoriteRequestCellRenderer"
import StatusCellRenderer from "components/ag-grid-cell-renderers/StatusCellRenderer/StatusCellRenderer"
import RegistryCellRenderer from "containers/studies/Study/subcomponents/Assessments/AssessmentsGrid/RegistryCellRenderer/RegistryCellRenderer"
import UserFullnameCellRenderer from "components/ag-grid-cell-renderers/UserFullnameCellRenderer/UserFullnameCellRenderer"
import FilzeSizeCellRenderer from "components/ag-grid-cell-renderers/FileSizeCellRenderer/FilzeSizeCellRenderer"
import SuccessFailedCellRenderer from "components/ag-grid-cell-renderers/SuccessFailedCellRenderer/SuccessFailedCellRenderer"
import CountriesCellRenderer from "components/ag-grid-cell-renderers/CountriesCellRenderer/CountriesCellRenderer"
import PrsValidationsWithCount from "components/ag-grid-cell-renderers/PrsValidationsWithCount/PrsValidationsWithCount"

// assets
import noRowsImg from "assets/images/empty-grid.png"

// styles
import "./GridTypeA.scss"

const GridTypeA = ({
  gridRef,
  className,
  style,
  rowData,
  columnDefs: columnDefsProps,
  pagination = false,
  paginationPageSize = 10,
  search,
  defaultColDef: defaultColDefProps,
  gridOptions: gridOptionProps,
  colWidth,
  domLayout = "normal",
  getRowHeight,
  rowSelection, // if row selection "multiple", rowMultiSelectWithClick and selection count will appear on status bar
  rowModelType,
  autoSizeStrategy = "fitCellContents",
  apiConfig,
  isWidget = false,

  // events
  onGridReady: onGridReadyProp,
  onFirstDataRendered: onFirstDataRenderedProp,
  onFilterChanged: onFilterChangedProp,

  // model state methods
  defaultModelState,
  modelState, // for controlled model, which only works with filtering at the moment; onModelStateChange must handle setting the variable
  onModelStateChange, // handles setting the modalState for a controlled filter model

  // status bar
  statusBar: statusBarProp,
  showRowCount = true,

  // sideBar and toolPanels
  sideBar: sideBarProp,
  presetGridName,
  complianceThreshold,

  ...restOfGridProps // serverSideDatasource
}) => {
  const [gridApi, setGridApi] = React.useState()

  React.useEffect(() => {
    if (gridApi) {
      gridApi.resetRowHeights()
    }
  }, [gridApi])

  React.useEffect(() => {
    if (gridApi && search !== undefined) {
      if (search) {
        gridApi.setQuickFilter(search)
      } else {
        gridApi.setQuickFilter("")
      }
    }
  }, [gridApi, search])

  // default model state when the grid is not controlled; used for preset model states
  React.useEffect(() => {
    if (gridApi && !modelState) {
      if (defaultModelState) {
        if (defaultModelState.column_state)
          gridApi.applyColumnState({ state: defaultModelState.column_state, applyOrder: true })
        if (defaultModelState.filter_state) {
          gridApi.setFilterModel(defaultModelState.filter_state)
        }
      } else {
        gridApi.setFilterModel(null)
        gridApi.resetColumnState()
        gridApi.resetColumnGroupState()
        gridApi.onFilterChanged()
      }
    }
  }, [gridApi, defaultModelState]) // defaultModelState

  // controlled filter model
  React.useEffect(() => {
    if (gridApi && modelState) {
      if (modelState.column_state) gridApi.applyColumnState({ state: modelState.column_state, applyOrder: true })
      if (modelState.filter_state) {
        gridApi.setFilterModel(modelState.filter_state)
        // gridApi.onFilterChanged() // not necessary for now
      }
    }
  }, [gridApi, modelState])

  // add tooltip to column definition
  // NOTE: set headerTooltip or headerName to empty string to remove header tooltip
  const getTooltipColDefs = (colDefs) => {
    const getHeaderTooltipDef = (colDef) => {
      const def = { ...colDef }
      // use headerTooltip if defined
      if (def.headerTooltip == undefined || def.headerTooltip === null) {
        // use headerName if defined
        if (def.headerName !== undefined && def.headerName !== null) {
          def.headerTooltip = def.headerName
        }
        // use field in sentence case if only one word and is defined
        else if (def.field) {
          def.headerTooltip = `${def.field?.[0].toUpperCase()}${def.field?.substring(1)}`
        }
      }
      if (def.children) {
        def.children = def.children.map((child) => getHeaderTooltipDef(child))
      }
      return def
    }
    if (colDefs) {
      return colDefs.map((def) => getHeaderTooltipDef(def))
    }
  }

  // add header tooltips to all headers from columnDefs
  const columnDefs = React.useMemo(() => {
    if (columnDefsProps) {
      return getTooltipColDefs(columnDefsProps)
    }
    return undefined
  }, [columnDefsProps])

  const NoRowsOverlayComponent = () => {
    return (
      <div className="no-rows-overlay-component">
        <img
          src={noRowsImg}
          alt="No rows image"
        />
      </div>
    )
  }

  const gridOptions = {
    icons: {
      presets: '<i class="fa fa-list" />',
      calicon: '<i class="fal fa-clock" />',
    },
    loadingOverlayComponent: () => <Loading position="relative" />,
    noRowsOverlayComponent: () => <NoRowsOverlayComponent />,
    tooltipShowDelay: 0.5,
    ...gridOptionProps,

    // add header tooltips to col defs
    columnDefs: gridOptionProps?.columnDefs ? getTooltipColDefs(gridOptionProps.columnDefs) : null,
  }

  const sideBar = React.useMemo(() => {
    if (sideBarProp || sideBarProp === false) return sideBarProp
    let toolPanels = [
      {
        id: "columns",
        labelDefault: "Columns",
        labelKey: "columns",
        iconKey: "columns",
        toolPanel: "agColumnsToolPanel",
        toolPanelParams: {
          suppressRowGroups: true,
          suppressValues: true,
          suppressPivots: true,
          suppressPivotMode: true,
        },
      },
      "filters",
    ]
    if (presetGridName) {
      toolPanels.push({
        id: "presets",
        labelDefault: "Presets",
        labelKey: "presets",
        iconKey: "presets",
        toolPanel: Presets,
        toolPanelParams: {
          gridName: presetGridName,
          apiConfig,
        },
      })
    }
    if (complianceThreshold) {
      toolPanels.push({
        id: "compliance-threshold",
        labelDefault: "Threshold",
        labelKey: "threshold",
        iconKey: "calicon",
        toolPanel: ComplianceThreshold,
      })
    }
    return {
      hiddenByDefault: false,
      toolPanels,
    }
  }, [presetGridName, complianceThreshold, JSON.stringify(apiConfig)])

  const defaultColDef = {
    filter: true,
    lockPosition: false,
    tooltipComponent: "customTooltip",
    enableRowGroup: false,
    enableValue: true, // to be able to aggregate by cols via the GUI
    enableRangeSelection: false,

    // wrap text and headers
    wrapText: true,
    autoHeight: true,
    wrapHeaderText: true,
    autoHeaderHeight: true,

    ...defaultColDefProps,
    filterParams: {
      buttons: ["clear"],
      ...defaultColDefProps?.filterParams,
    },
  }

  const components = {
    Date,
    DateSecond,
    ActionItemStatus,
    StudiesCellRenderer,
    RequestsCellRenderer,
    FavoriteStudyCellRenderer,
    FavoriteRequestCellRenderer,
    StatusCellRenderer,
    RegistryCellRenderer,
    UserFullnameCellRenderer,
    FilzeSizeCellRenderer,
    SuccessFailedCellRenderer,
    CountriesCellRenderer,
    PrsValidationsWithCount,
    // badge cell renderers
    PhaseBadge: Badge.Phase,
    StudyTypeBadge: Badge.StudyType,
    RecordStatusBadge,
    WorkflowStatusBadge: WorkflowStatusBadgeCell,
  }

  const columnTypes = {
    dateColumn,
  }

  // grid events
  const onGridReady = (params) => {
    const { api } = params
    setGridApi(api)
    if (onGridReadyProp) onGridReadyProp(params)
    if (onFirstDataRenderedProp) onFirstDataRenderedProp(params)
  }

  const getAutoSizeStrategy = () => {
    if (autoSizeStrategy === "fitGridWidth") {
      return { type: "fitGridWidth" }
    }
    if (autoSizeStrategy === "fitCellContents") {
      let strategy = { type: "fitCellContents", skipHeader: true }
      // set autoSizeStrategy for CSRM
      if (rowModelType === "clientSide" || !rowModelType) {
        return strategy
      }
      // for SSRM, if no cols have width, set autoSizeStrategy
      //   otherwise, auto-size on firstDataRendered event instead
      else {
        const colDefs = columnDefsProps || gridOptionProps?.columnDefs
        if (colDefs && !colDefs.some((col) => col?.width)) {
          return strategy
        }
      }
    }
    return null
  }

  // called on firstDataRendered
  const autoSizeColumns = (props) => {
    const { api } = props
    if (api) {
      // if grid's autoSizeStrategy set to "fitCellContents" and SSRM, auto-size columns without a "width" prop
      if (autoSizeStrategy === "fitCellContents" && rowModelType === "serverSide") {
        let resizeCols = []
        let fieldWidths = []
        columnDefs.forEach((col) => {
          if (!col.width && col.field) {
            resizeCols.push(col.field)
          } else if (col.width && col.field) {
            fieldWidths.push({ key: col.field, newWidth: col.width })
          }
        })
        if (resizeCols.length > 0 && api?.autoSizeColumns) {
          api.autoSizeColumns(resizeCols, true)
          // resize column widths for those that have "width" prop set
          if (fieldWidths.length > 0) {
            api.setColumnWidths(fieldWidths)
          }
        }
      }
      if (defaultModelState) {
        if (defaultModelState.column_state) {
          api.applyColumnState({ state: defaultModelState.column_state, applyOrder: true })
        }
        if (defaultModelState.filter_state) {
          api.setFilterModel(defaultModelState.filter_state)
        }
      }
    }
  }

  const autoResizeCleanup = (props) => {
    const { columns, api, source } = props
    // show console error when cells have flex property set, which messes with the fit-cell-content strategy
    if (source === "flex") {
      let defs = columnDefsProps || gridOptionProps?.columnDefs
      console.error('Warning: this grid has "flex" settings and will auto-size with the flex strategy:', {
        ["columns with flex"]: defs.filter((col) => col.flex),
        colDefs: defs,
      })
    }
    // Expand width of cells with narrow widths
    if (source !== "uiColumnResized") {
      // "autosizeColumns", "api"
      let fieldWidths = []
      columns.forEach((col) => {
        if (!col.colDef?.width && col.actualWidth < 100) {
          // get width to accommodate the longest word in header name
          const longestWord = col.colDef?.headerName?.split(" ").sort((a, b) => b.length - a.length)[0]
          const getWidth = (word) => {
            if (word) {
              // the "ratio" is the number of pixels per letter
              let ratio = 17
              // TODO: come up with function to get a ratio based on the number of letters?
              if (word.length <= 5) ratio = 22
              return word.length * ratio
            } else {
              return 110
            }
          }
          const newWidth = getWidth(longestWord)
          if (col.actualWidth < newWidth) {
            fieldWidths.push({ key: col.colId, newWidth })
          }
        }
      })
      if (fieldWidths.length > 0) {
        setTimeout(() => {
          api.setColumnWidths(fieldWidths)
        }, 100)
      }
    }
  }

  const statusBar = React.useMemo(() => {
    if (statusBarProp === null) return null // intentionally remove status bar // to remove row count, set showRowCount to false
    let bar = {}
    bar.statusPanels = []
    if (statusBarProp?.statusPanels) {
      bar.statusPanels = [...statusBarProp.statusPanels]
    }
    // show row count by default, unless there is pagination
    if (showRowCount && !pagination && rowModelType !== "serverSide")
      bar.statusPanels.push({
        statusPanel: "agTotalAndFilteredRowCountComponent",
      })
    if (rowSelection)
      bar.statusPanels.push({
        statusPanel: "agSelectedRowCountComponent",
        align: "left",
      })
    return bar
  }, [statusBarProp])

  const getModel = (api) => {
    if (api) {
      const column_state = api.getColumnState()
      const filter_state = api.getFilterModel()
      const sort_state = column_state?.reduce((acc, state) => {
        if (state.sort) acc.push(state)
        return acc
      }, [])
      return {
        filter_state,
        column_state,
        sort_state,
      }
    }
    return {}
  }

  return (
    <div
      className={`ag-theme-quartz grid-type-a ${className ? " " + className : ""}`}
      style={style}
    >
      <AgGridReact
        ref={gridRef}
        rowData={rowData && Array.isArray(rowData) ? rowData : undefined} // not sent if rowModelType="serverSide"
        gridOptions={gridOptions}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        columnTypes={columnTypes}
        sideBar={sideBar}
        pagination={pagination}
        paginationPageSize={paginationPageSize}
        // row selection
        rowSelection={rowSelection}
        rowMultiSelectWithClick={rowSelection === "multiple"}
        suppressRowClickSelection={true}
        getRowHeight={getRowHeight} // will override rowHeight prop
        domLayout={domLayout}
        statusBar={statusBar}
        components={components}
        rowModelType={rowModelType}
        autoSizeStrategy={getAutoSizeStrategy()}
        //events
        onGridReady={onGridReady}
        onFirstDataRendered={autoSizeColumns}
        onFilterChanged={(params) => {
          const { api } = params
          if (onFilterChangedProp) onFilterChangedProp(params)
          if (onModelStateChange) {
            const model = getModel(api)
            onModelStateChange(params, model)
          }
        }}
        // TODO: if need to control column sorting update with onSortChanged
        // TODO: if need to control column pinning update with onColumnPinned
        // TODO: if need to control column hiding update with onColumnVisible
        onColumnResized={autoResizeCleanup}
        {...restOfGridProps}
      />
    </div>
  )
}

GridTypeA.propTypes = {
  search: PropTypes.string,
  rowSelection: PropTypes.oneOf(["single", "multiple"]),
  defaultModelState: PropTypes.shape({
    column_state: PropTypes.arrayOf(
      PropTypes.shape({
        colId: PropTypes.string,
        hide: PropTypes.bool,
        pinned: PropTypes.oneOf(["left", "right"]),
        sort: PropTypes.oneOf(["asc", "desc"]),
      })
    ),
    filter_state: PropTypes.object,
    sort_state: PropTypes.arrayOf(
      PropTypes.shape({
        colId: PropTypes.string,
        sort: PropTypes.oneOf(["asc", "desc"]),
      })
    ),
  }),
  /**
   * column_state is the same as in defaultModelState
   */
  modelState: PropTypes.shape({
    column_state: PropTypes.object,
    filter_state: PropTypes.object,
  }),
  onModelStateChange: PropTypes.func,
  rowData: PropTypes.arrayOf(PropTypes.object),
  /**
   * With "normal" layout, the height of the grid will be that of its parent. With "autoHeight", the height increases depending on number of rows.
   */
  domLayout: PropTypes.oneOf(["normal", "autoHeight"]),
  showRowCount: PropTypes.bool,
  showSelectedCount: PropTypes.bool,
  presetGridName: PropTypes.string,
  /**
   * "infinite" and "viewport" row models not used in Prime
   */
  rowModelType: PropTypes.oneOf(["clientSide", "serverSide"]),
  autoSizeStrategy: PropTypes.oneOf(["fitGridWidth", "fitCellContents"]),
}

export default GridTypeA
